import {
  ConsentForm,
  FileDBValue,
  InputTemplateAttachment,
  TemplateAttachment,
  WithId,
  populateConsentFormField,
} from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'
import { useCachedUser } from '../../../collections/hooks/cached'
import { FileValuePdfView } from '../../PdfView'
import { EditorSize } from '../../RichText'
import { OnSubmitForm } from '../../RichText/DocumentFormProvider'

export const ConsentFormPdfView = ({
  consentForm,
  editorSize,
  children,
  onFormSubmit,
  withDownload,
}: PropsWithChildren<{
  consentForm: WithId<ConsentForm>
  editorSize: EditorSize
  onFormSubmit?: OnSubmitForm
  withDownload?: boolean
}>) => {
  const { externalPdf, signedStoragePath, signedOn, toBeSignedByUser: toBeSignedById } = consentForm
  const { data: toBeSignedByUser } = useCachedUser(toBeSignedById)
  const signedValue = useMemo<FileDBValue | null>(
    () =>
      signedStoragePath && signedOn
        ? { storagePath: signedStoragePath, type: 'application/pdf', uploadedOn: signedOn, id: '' }
        : null,
    [signedStoragePath, signedOn],
  )
  const populatedAttachments = useMemo(
    () =>
      toBeSignedByUser
        ? Object.entries(externalPdf.attachments).reduce<Record<string, TemplateAttachment>>(
            (acc, [key, attachment]) => {
              if (attachment.type === 'input') {
                return {
                  ...acc,
                  [key]: {
                    ...attachment,
                    field: populateConsentFormField(attachment.field, toBeSignedByUser),
                  } as InputTemplateAttachment,
                }
              }
              return {
                ...acc,
                [key]: attachment,
              }
            },
            {},
          )
        : externalPdf.attachments,
    [externalPdf, toBeSignedByUser],
  )

  if (signedStoragePath) {
    return (
      <FileValuePdfView
        withDownload={withDownload}
        value={signedValue}
        height={editorSize.height}
        document={consentForm}
        width={editorSize.width}>
        {children}
      </FileValuePdfView>
    )
  }

  return (
    <FileValuePdfView
      value={externalPdf.baseFile}
      attachments={populatedAttachments}
      document={consentForm}
      withDownload={withDownload}
      onFormSubmit={onFormSubmit}
      height={editorSize.height}
      width={editorSize.width}>
      {children}
    </FileValuePdfView>
  )
}
